import {
    CheckOutlined,
    ExclamationCircleFilled,
    CalendarOutlined,
    BankOutlined,
    WarningOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    PrinterOutlined,
    CheckCircleOutlined,
    MinusCircleOutlined,
    DollarOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    DatePicker,
    Modal,
    Select,
    Skeleton,
    Tabs,
    Card,
    Table,
    Space,
    Button,
    Row,
    Col,
    Badge,
    Divider
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useReducer, useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import { frenchMonth } from '../../../variables/functions';
import { displayNumbers } from '../../../variables/numbersFunctions';
import dayjs from 'dayjs';
import RetrocessionStyle from './RetrocessionStyle';
import { annulationsColumns, cessionParCompagnieColumns, emissionsColumns, recoursColumns, sinistresColumns } from './RetrocessionData';
import { BordereauParCompagnie } from './BordereauParCompagnie';
import {
    calculerRecapitulatifGlobal,
    grouperCessionsParCompagnie,
    traiterAnnulationsCompagnie,
    traiterEmissionsCompagnie,
    traiterRecoursCompagnie,
    traiterSinistresCompagnie
} from './retrocessionHooks';

const { confirm } = Modal;

// Reducer pour gérer l'état des validations
const validationReducer = (state, action) => {
    switch (action.type) {
        case 'VALIDATE_EMISSIONS':
            return { ...state, emissions: { validated: true, data: action.payload } };
        case 'VALIDATE_ANNULATIONS':
            return { ...state, annulations: { validated: true, data: action.payload } };
        case 'VALIDATE_SINISTRES':
            return { ...state, sinistresPayes: { validated: true, data: action.payload } };
        case 'VALIDATE_RECOURS':
            return { ...state, recoursEncaisses: { validated: true, data: action.payload } };
        case 'VALIDATE_RECAP':
            return { ...state, recapValidated: true };
        case 'RESET':
            return initialValidationState;
        default:
            return state;
    }
};

const initialValidationState = {
    emissions: { validated: false, data: [] },
    annulations: { validated: false, data: [] },
    sinistresPayes: { validated: false, data: [] },
    recoursEncaisses: { validated: false, data: [] },
    recapValidated: false
};

const Retrocession = () => {
    // États pour les filtres
    const [categories, setCategories] = useState([]);
    const [branches, setBranches] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState("");
    const [compagnies, setCompagnies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [moisRetrocession, setMoisRetrocession] = useState(null);
    const [dateLimiteCession, setDateLimiteCession] = useState(null);
    // SUPPRIMÉ : clesRepartition n'est plus nécessaire
    const [penalites, setPenalites] = useState([]);

    // États pour les données de cession par compagnie
    const [cessionsParCompagnie, setCessionsParCompagnie] = useState([]);
    const [retrocessionExistante, setRetrocessionExistante] = useState(null);
    const [loadingRetrocession, setLoadingRetrocession] = useState(false);

    // État de validation
    const [validationState, dispatch] = useReducer(validationReducer, initialValidationState);
    const [tabId, setTabId] = useState("1");

    // États pour les Collapse
    const [activeEmissionsKeys, setActiveEmissionsKeys] = useState([]);
    const [activeAnnulationsKeys, setActiveAnnulationsKeys] = useState([]);
    const [activeSinistresKeys, setActiveSinistresKeys] = useState([]);
    const [activeRecoursKeys, setActiveRecoursKeys] = useState([]);

    useEffect(() => {
        Promise.all([
            axiosInstance.get(`/api/categorie`),
            axiosInstance.get(`/api/branche`),
            axiosInstance.get(`/api/organisme/assurance`)
        ])
            .then(([catRes, brancheRes, compRes]) => {
                const excludedCodes = ["A", "B", "T"];
                const filtered = catRes.data.filter(
                    (c) => !c.codeCategorie.startsWith("V") && !excludedCodes.includes(c.codeCategorie)
                );

                const hasTPVCategories = catRes.data.some((c) => excludedCodes.includes(c.codeCategorie));
                if (hasTPVCategories) {
                    filtered.push({ codeCategorie: "TPV", nom: "TPV", synthetic: true });
                }

                setCategories(filtered);
                setBranches(brancheRes.data);
                setCompagnies(compRes.data);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des données");
                setLoading(false);
            });

        chargerParametresPenalites();
    }, []);

    useEffect(() => {
        if (selectedCategorie && moisRetrocession) {
            // SUPPRIMÉ : chargerClesRepartition() n'est plus nécessaire
            chargerCessionsValidees();
            verifierRetrocessionExistante();
        }
    }, [selectedCategorie, moisRetrocession]);

    const chargerParametresPenalites = () => {
        axiosInstance
            .get('/api/parametre-penalite')
            .then((res) => setPenalites(res.data))
            .catch((err) => {
                console.error(err);
                toast.warning("Paramètres de pénalités non configurés");
            });
    };

    // SUPPRIMÉ : chargerClesRepartition() - plus nécessaire

    const chargerCessionsValidees = () => {
        setLoading(true);
        axiosInstance
            .get('/api/cession/validees-avec-details-et-categorie', {
                params: {
                    mois: moisRetrocession,
                    categorieCode: selectedCategorie
                }
            })
            .then((res) => {
                const cessionsGroupees = grouperCessionsParCompagnie(res.data, dateLimiteCession, penalites, moisRetrocession);
                console.log("Cessions groupées par compagnie :", cessionsGroupees);
                setCessionsParCompagnie(cessionsGroupees);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des cessions");
                setLoading(false);
            });
    };

    const verifierRetrocessionExistante = () => {
        axiosInstance
            .get('/api/retrocession/verifier', {
                params: {
                    mois: moisRetrocession + "-01",
                    categorie: selectedCategorie
                }
            })
            .then((res) => {
                if (res.data) {
                    setRetrocessionExistante(res.data);
                    dispatch({ type: 'VALIDATE_RECAP' });
                }
            })
            .catch((err) => console.error(err));
    };

    // =========================================================================
    // Fonctions de validation (simplifiées : plus de clesRepartition)
    // =========================================================================

    const confirmValidationEmissions = () => {
        confirm({
            title: "Validation du bordereau des émissions rétrocédées",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les émissions avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const emissionsGlobales = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const { emissionsRetrocedees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
                    emissionsGlobales.push(...emissionsRetrocedees);
                });
                dispatch({ type: 'VALIDATE_EMISSIONS', payload: emissionsGlobales });
                toast.success("Bordereau des émissions validé");
            },
        });
    };

    const confirmValidationAnnulations = () => {
        confirm({
            title: "Validation du bordereau des annulations rétrocédées",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les annulations avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const annulationsGlobales = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const annulationsRetrocedees = traiterAnnulationsCompagnie(compagnie, moisRetrocession);
                    annulationsGlobales.push(...annulationsRetrocedees);
                });
                dispatch({ type: 'VALIDATE_ANNULATIONS', payload: annulationsGlobales });
                toast.success("Bordereau des annulations validé");
            },
        });
    };

    const confirmValidationSinistres = () => {
        confirm({
            title: "Validation du bordereau des sinistres payés rétrocédés",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les sinistres avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const sinistresGlobaux = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const sinistresRetrocedes = traiterSinistresCompagnie(compagnie, moisRetrocession);
                    sinistresGlobaux.push(...sinistresRetrocedes);
                });
                dispatch({ type: 'VALIDATE_SINISTRES', payload: sinistresGlobaux });
                toast.success("Bordereau des sinistres validé");
            },
        });
    };

    const confirmValidationRecours = () => {
        confirm({
            title: "Validation du bordereau des recours encaissés rétrocédés",
            icon: <ExclamationCircleFilled />,
            content: "Voulez-vous valider les recours avec les répartitions calculées ?",
            okText: "Confirmer",
            cancelText: "Annuler",
            onOk() {
                const recoursGlobaux = [];
                cessionsParCompagnie.forEach(compagnie => {
                    const recoursRetrocedes = traiterRecoursCompagnie(compagnie, moisRetrocession);
                    recoursGlobaux.push(...recoursRetrocedes);
                });
                dispatch({ type: 'VALIDATE_RECOURS', payload: recoursGlobaux });
                toast.success("Bordereau des recours validé");
            },
        });
    };

    const confirmValidationRecapitulatif = () => {
        confirm({
            title: "Validation du récapitulatif global",
            icon: <ExclamationCircleFilled />,
            content: "Cette action clôturera la rétrocession pour cette période. Confirmez-vous ?",
            okText: "Valider et clôturer",
            okType: "danger",
            cancelText: "Annuler",
            onOk() {
                validerRetrocession();
            },
        });
    };

    const validerRetrocession = () => {
        setLoadingRetrocession(true);

        const recap = calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession);

        // 1. Collecter toutes les données traitées depuis toutes les cédantes
        const allEmissions = [];
        const allEmissionsReportees = [];
        const allAnnulations = [];
        const allSinistres = [];
        const allRecours = [];

        cessionsParCompagnie.forEach(compagnie => {
            const { emissionsRetrocedees, emissionsReportees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
            allEmissions.push(...emissionsRetrocedees);
            allEmissionsReportees.push(...emissionsReportees);
            allAnnulations.push(...traiterAnnulationsCompagnie(compagnie, moisRetrocession));
            allSinistres.push(...traiterSinistresCompagnie(compagnie, moisRetrocession));
            allRecours.push(...traiterRecoursCompagnie(compagnie, moisRetrocession));
        });

        // 2. Calculer les totaux par compagnie BÉNÉFICIAIRE (identique à l'UI)
        const retrocessionParCompagnie = compagniesRepartition.map(comp => {
            const totalEmissions = allEmissions.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            const totalAnnulations = allAnnulations.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            const totalSinistres = allSinistres.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            const totalRecours = allRecours.reduce((sum, item) => {
                const rep = item.repartition?.find(r => r.compagnieCode === comp.code);
                return sum + (rep?.montant || 0);
            }, 0);

            // Pénalité : chercher la cédante correspondante (si cette bénéficiaire est aussi cédante)
            const cedante = cessionsParCompagnie.find(c => c.compagnie.code === comp.code);

            return {
                compagnie: comp,
                emissions: allEmissions,
                emissionsReportees: allEmissionsReportees,
                annulations: allAnnulations,
                sinistres: allSinistres,
                recours: allRecours,
                penalite: cedante?.penalite || 0,
                joursRetard: cedante?.joursRetard || 0,
                totalEmissions,
                totalAnnulations,
                totalSinistres,
                totalRecours,
            };
        });

        const retrocessionData = {
            utilisateur: LOGGED_USER.codeId,
            moisRetrocession: moisRetrocession + "-01",
            dateLimiteCession,
            branche: branches.find(c => c.codeBranche === selectedCategorie),
            retrocessionParCompagnie,
            recapitulatifGlobal: recap
        };

        console.log("Données envoyées pour validation de la rétrocession :", retrocessionData);

        axiosInstance
            .post('/api/retrocession', retrocessionData)
            .then((res) => {
                dispatch({ type: 'VALIDATE_RECAP' });
                toast.success("Rétrocession validée et clôturée avec succès!");
                setLoadingRetrocession(false);
                setRetrocessionExistante(res.data);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors de la validation de la rétrocession");
                setLoadingRetrocession(false);
            });
    };

    const onTabsChange = (key) => {
        const allBordereauxValidated =
            validationState.emissions.validated &&
            validationState.annulations.validated &&
            validationState.sinistresPayes.validated &&
            validationState.recoursEncaisses.validated;

        if (key === "5" && !allBordereauxValidated && !retrocessionExistante) {
            toast.warning("Veuillez valider tous les bordereaux avant d'accéder au récapitulatif");
            return;
        }
        setTabId(key);
    };

    const validerDateLimite = (date) => {
        if (!moisRetrocession) {
            toast.warning("Veuillez d'abord sélectionner le mois de rétrocession");
            return false;
        }

        const finMois = dayjs(moisRetrocession).endOf('month');
        const dateLimite = dayjs(date);

        if (!dateLimite.isAfter(finMois)) {
            toast.error("La date limite doit être strictement postérieure à la fin du mois sélectionné");
            return false;
        }

        return true;
    };

    const isRetrocessionValidee = validationState.recapValidated || retrocessionExistante;

    // Extraire la liste unique des compagnies depuis les données de répartition
    const getCompagniesFromRepartition = () => {
        const compagniesSet = new Map();

        cessionsParCompagnie.forEach(compagnie => {
            // Parcourir tous les bordereaux pour extraire les compagnies
            const allItems = [
                ...(compagnie.emissions || []),
                ...(compagnie.annulations || []),
                ...(compagnie.sinistres || []),
                ...(compagnie.recours || [])
            ];

            allItems.forEach(item => {
                item.repartition?.forEach(rep => {
                    if (!compagniesSet.has(rep.compagnieCode)) {
                        compagniesSet.set(rep.compagnieCode, {
                            code: rep.compagnieCode,
                            raisonSociale: rep.compagnieNom
                        });
                    }
                });
            });
        });

        return Array.from(compagniesSet.values()).sort((a, b) =>
            a.raisonSociale.localeCompare(b.raisonSociale)
        );
    };

    const compagniesRepartition = getCompagniesFromRepartition();

    return (
        <div className="layout-wrapper layout-content-navbar">
            <RetrocessionStyle />

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: 'var(--retro-bg)' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* Header */}
                            <div className="retrocession-page-header">
                                <h1>🔄 Rétrocession</h1>
                                <p>
                                    Gestion de la rétrocession mensuelle avec répartitions pré-calculées et calcul des pénalités de retard
                                </p>
                            </div>

                            {/* Filtres */}
                            <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <CalendarOutlined />
                                                Mois de rétrocession
                                            </div>
                                            <DatePicker
                                                placeholder="Sélectionner"
                                                onChange={(_, dateString) => setMoisRetrocession(dateString)}
                                                picker="month"
                                                disabled={isRetrocessionValidee}
                                                style={{ width: '100%' }}
                                                size="large"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <ClockCircleOutlined />
                                                Date limite cession
                                            </div>
                                            <DatePicker
                                                placeholder="Date limite"
                                                onChange={(date, dateString) => {
                                                    if (validerDateLimite(date)) {
                                                        setDateLimiteCession(dateString);
                                                    }
                                                }}
                                                disabled={!moisRetrocession || isRetrocessionValidee}
                                                style={{ width: '100%' }}
                                                size="large"
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <FileTextOutlined />
                                                Branche
                                            </div>
                                            <Select
                                                placeholder="Sélectionner"
                                                style={{ width: "100%" }}
                                                onChange={setSelectedCategorie}
                                                disabled={isRetrocessionValidee}
                                                size="large"
                                                options={branches.map((br) => ({
                                                    value: br.codeBranche,
                                                    label: br.nom,
                                                }))}
                                            />
                                        </div>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} lg={6}>
                                    <div className="filter-card">
                                        <div className="card-body">
                                            <div className="filter-label">
                                                <ReloadOutlined />
                                                Actions
                                            </div>
                                            <Button
                                                type="primary"
                                                icon={<ReloadOutlined />}
                                                onClick={chargerCessionsValidees}
                                                disabled={!selectedCategorie || !moisRetrocession}
                                                style={{
                                                    width: '100%',
                                                    height: 40,
                                                    background: 'var(--retro-primary)'
                                                }}
                                                size="large"
                                            >
                                                Actualiser
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Tableau de suivi */}
                            {cessionsParCompagnie.length > 0 && (
                                <Card
                                    title={
                                        <Space>
                                            <BankOutlined />
                                            <span>Suivi des cessions par compagnie</span>
                                            <Badge count={cessionsParCompagnie.length} style={{ backgroundColor: 'var(--retro-primary)' }} />
                                        </Space>
                                    }
                                    className="tracking-card"
                                >
                                    <Table
                                        columns={cessionParCompagnieColumns(dateLimiteCession)}
                                        dataSource={cessionsParCompagnie}
                                        rowKey={(record) => record.compagnie.code}
                                        pagination={false}
                                        size="middle"
                                    />
                                </Card>
                            )}

                            {/* Contenu principal */}
                            <div className="main-card">
                                <div className="main-card-header">
                                    <h2>
                                        Rétrocession - {selectedCategorie || "N/A"} - {moisRetrocession ? frenchMonth(moisRetrocession.split("-")[1]) + " " + moisRetrocession.split("-")[0] : "N/A"}
                                    </h2>
                                    {isRetrocessionValidee && (
                                        <span className="status-badge">
                                            <CheckOutlined />
                                            Rétrocession clôturée
                                        </span>
                                    )}
                                </div>

                                <div className="card-body" style={{ padding: 24 }}>
                                    <ConfigProvider locale={frFR}>
                                        <Skeleton loading={loading} active>
                                            <div className="retro-tabs">
                                                <Tabs
                                                    onChange={onTabsChange}
                                                    activeKey={tabId}
                                                    items={[
                                                        {
                                                            label: (
                                                                <span>
                                                                    Émissions
                                                                    {validationState.emissions.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "1",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="emissions"
                                                                    columns={emissionsColumns(compagniesRepartition)}
                                                                    activeKeys={activeEmissionsKeys}
                                                                    setActiveKeys={setActiveEmissionsKeys}
                                                                    processData={(compagnie) => {
                                                                        const { emissionsRetrocedees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
                                                                        return {
                                                                            data: emissionsRetrocedees,
                                                                            totalOriginal: emissionsRetrocedees.reduce((sum, e) => sum + (e.montantOriginal || 0), 0),
                                                                            totalRetrocede: emissionsRetrocedees.reduce((sum, e) => sum + (e.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationEmissions}
                                                                    isValidated={validationState.emissions.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: (
                                                                <span>
                                                                    Annulations
                                                                    {validationState.annulations.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "2",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="annulations"
                                                                    columns={annulationsColumns(compagniesRepartition)}
                                                                    activeKeys={activeAnnulationsKeys}
                                                                    setActiveKeys={setActiveAnnulationsKeys}
                                                                    processData={(compagnie) => {
                                                                        const annulationsRetrocedees = traiterAnnulationsCompagnie(compagnie, moisRetrocession);
                                                                        return {
                                                                            data: annulationsRetrocedees,
                                                                            totalOriginal: annulationsRetrocedees.reduce((sum, a) => sum + (a.montantOriginal || 0), 0),
                                                                            totalRetrocede: annulationsRetrocedees.reduce((sum, a) => sum + (a.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationAnnulations}
                                                                    isValidated={validationState.annulations.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: (
                                                                <span>
                                                                    Sinistres payés
                                                                    {validationState.sinistresPayes.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "3",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="sinistres"
                                                                    columns={sinistresColumns(compagniesRepartition)}
                                                                    activeKeys={activeSinistresKeys}
                                                                    setActiveKeys={setActiveSinistresKeys}
                                                                    processData={(compagnie) => {
                                                                        const sinistresRetrocedes = traiterSinistresCompagnie(compagnie, moisRetrocession);
                                                                        return {
                                                                            data: sinistresRetrocedes,
                                                                            totalOriginal: sinistresRetrocedes.reduce((sum, s) => sum + (s.montantOriginal || 0), 0),
                                                                            totalRetrocede: sinistresRetrocedes.reduce((sum, s) => sum + (s.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationSinistres}
                                                                    isValidated={validationState.sinistresPayes.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: (
                                                                <span>
                                                                    Recours encaissés
                                                                    {validationState.recoursEncaisses.validated && (
                                                                        <span className="tab-icon-validated">✓</span>
                                                                    )}
                                                                </span>
                                                            ),
                                                            key: "4",
                                                            children: (
                                                                <BordereauParCompagnie
                                                                    type="recours"
                                                                    columns={recoursColumns(compagniesRepartition)}
                                                                    activeKeys={activeRecoursKeys}
                                                                    setActiveKeys={setActiveRecoursKeys}
                                                                    processData={(compagnie) => {
                                                                        const recoursRetrocedes = traiterRecoursCompagnie(compagnie, moisRetrocession);
                                                                        return {
                                                                            data: recoursRetrocedes,
                                                                            totalOriginal: recoursRetrocedes.reduce((sum, r) => sum + (r.montantOriginal || 0), 0),
                                                                            totalRetrocede: recoursRetrocedes.reduce((sum, r) => sum + (r.montantRetrocede || 0), 0)
                                                                        };
                                                                    }}
                                                                    onValidate={confirmValidationRecours}
                                                                    isValidated={validationState.recoursEncaisses.validated}
                                                                    isDisabled={isRetrocessionValidee}
                                                                    cessionsParCompagnie={cessionsParCompagnie}
                                                                    compagniesRepartition={compagniesRepartition}
                                                                    setActiveEmissionsKeys={setActiveEmissionsKeys}
                                                                    setActiveAnnulationsKeys={setActiveAnnulationsKeys}
                                                                    setActiveSinistresKeys={setActiveSinistresKeys}
                                                                    setActiveRecoursKeys={setActiveRecoursKeys}
                                                                    loadingRetrocession={loadingRetrocession}
                                                                />
                                                            ),
                                                        },
                                                        {
                                                            label: "📊 Récapitulatif",
                                                            key: "5",
                                                            children: (
                                                                <div className="recap-container">
                                                                    <div className="recap-grid">
                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#f6ffed', color: '#52c41a' }}>
                                                                                    <CheckCircleOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Émissions</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#52c41a' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession).totalEmissions)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#fffbe6', color: '#faad14' }}>
                                                                                    <MinusCircleOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Annulations</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#faad14' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession).totalAnnulations)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#fff2f0', color: '#ff4d4f' }}>
                                                                                    <WarningOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Sinistres</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#ff4d4f' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession).totalSinistres)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#e6fffb', color: '#13c2c2' }}>
                                                                                    <DollarOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Recours</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#13c2c2' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession).totalRecours)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: '#fff1f0', color: '#cf1322' }}>
                                                                                    <ClockCircleOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Pénalités</span>
                                                                            </div>
                                                                            <div className="recap-card-value" style={{ color: '#cf1322' }}>
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession).totalPenalites)} F
                                                                            </div>
                                                                        </div>

                                                                        <div className="recap-card recap-total-card">
                                                                            <div className="recap-card-header">
                                                                                <div className="recap-card-icon" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>
                                                                                    <BankOutlined />
                                                                                </div>
                                                                                <span className="recap-card-title">Solde net</span>
                                                                            </div>
                                                                            <div className="recap-card-value">
                                                                                {displayNumbers(calculerRecapitulatifGlobal(cessionsParCompagnie, moisRetrocession).solde)} F
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <Divider />

                                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                                                                        <Button
                                                                            icon={<PrinterOutlined />}
                                                                            size="large"
                                                                        >
                                                                            Imprimer
                                                                        </Button>
                                                                        <Button
                                                                            type="primary"
                                                                            danger
                                                                            size="large"
                                                                            icon={<CheckOutlined />}
                                                                            onClick={confirmValidationRecapitulatif}
                                                                            disabled={isRetrocessionValidee}
                                                                            loading={loadingRetrocession}
                                                                        >
                                                                            Valider et clôturer
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            ),
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        </Skeleton>
                                    </ConfigProvider>
                                </div>
                            </div>
                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default Retrocession;